/**
 * Popover placement for `SearchableDropdown`.
 *
 * Extracted from the component so it can be tested without a DOM. The menu is
 * rendered in a portal at fixed coordinates, so nothing in CSS keeps it on
 * screen — a trigger near the right edge used to push the menu partly outside
 * the viewport, and a long option list was unreachable rather than merely ugly.
 *
 * This is shared by all 14 screens that mount a `SearchableDropdown`, so the
 * rules here are deliberately boring: clamp, never reflow.
 */

/** Narrowest a `SearchableDropdown` menu may be, and the width a trigger
 *  inherits when it is narrower than this. Matches the `min-width` the option
 *  rows plus the search box are laid out for. */
export const MENU_MIN_WIDTH_PX = 240;

/** `MultiSelect` carries a checkbox column and a footer (Clear, Select all
 *  shown, the count, Done), so it sits wider. Preserved from the value it used
 *  when it computed its own position. */
export const MULTI_MENU_MIN_WIDTH_PX = 280;

/** The plain `Dropdown` has no search row, so it sits narrower. Preserved from
 *  the value it used before this math was shared. */
export const DROPDOWN_MIN_WIDTH_PX = 200;

/** Breathing room kept between the menu and each viewport edge. */
export const VIEWPORT_MARGIN_PX = 8;

/** Gap between the trigger's bottom edge and the top of the menu. */
export const MENU_OFFSET_PX = 4;

export interface TriggerRect {
  left: number;
  bottom: number;
  width: number;
  /** Optional; derived from `left + width` when absent. `DOMRect` supplies it. */
  right?: number;
}

export interface MenuPosition {
  top: number;
  left: number;
  width: number;
}

export interface MenuPlacement {
  /** Narrowest the menu may be. Defaults to [`MENU_MIN_WIDTH_PX`]. */
  minWidth?: number;
  /** `'right'` aligns the menu's right edge to the trigger's. */
  align?: 'left' | 'right';
}

/**
 * Where to put the menu for a trigger at `rect` in a viewport `viewportWidth`
 * wide.
 *
 * `popWidth` is an explicit override from the caller. It is honoured as a
 * *request*, not a guarantee: a request wider than the viewport is clamped.
 * Note `popWidth: 0` means zero — it is not treated as "unset", because a
 * truthiness check made a legitimate 0 silently become the trigger width.
 *
 * Right-alignment resolves against the width this function actually returns.
 * The plain `Dropdown` used to subtract a hard-coded 240 while sizing itself to
 * `max(triggerWidth, 200)`, so a right-aligned menu was misaligned by the
 * difference whenever those disagreed — which was most of the time.
 *
 * Caveat worth knowing: `.dd-pop` applies this as `min-width` with
 * `white-space: nowrap`, so a long option label can render the menu wider than
 * `width`. The clamp is then approximate on the right edge. Measuring the
 * rendered menu would need a second layout pass; the labels in use are short
 * enough that the difference does not reach the viewport edge.
 */
export function menuPosition(
  rect: TriggerRect,
  popWidth: number | undefined,
  viewportWidth: number,
  placement: MenuPlacement = {},
): MenuPosition {
  const { minWidth = MENU_MIN_WIDTH_PX, align = 'left' } = placement;
  const requestedWidth = typeof popWidth === 'number'
    ? popWidth
    : Math.max(rect.width, minWidth);
  const availableWidth = Math.max(
    minWidth,
    viewportWidth - VIEWPORT_MARGIN_PX * 2,
  );
  const width = Math.min(requestedWidth, availableWidth);
  const right = rect.right ?? rect.left + rect.width;
  const preferredLeft = align === 'right' ? right - width : rect.left;
  // Slide only as far as needed to keep the right edge on screen, and never
  // past the left margin.
  const left = Math.max(
    VIEWPORT_MARGIN_PX,
    Math.min(preferredLeft, viewportWidth - width - VIEWPORT_MARGIN_PX),
  );
  return { top: rect.bottom + MENU_OFFSET_PX, left, width };
}

/** Below this much room under its trigger, a menu opens upward when there is
 *  more room above. */
export const MENU_MIN_USEFUL_HEIGHT_PX = 200;

export interface MenuVertical {
  /** Exactly one of `top` (opens downward) and `bottom` (opens upward, its
   *  bottom edge this far above the viewport's bottom) is set, so a short menu
   *  still hugs its trigger either way. */
  top?: number;
  bottom?: number;
  maxHeight: number;
}

/**
 * Which way a menu opens, and how tall it may be, for a trigger at `rect` in a
 * viewport `viewportHeight` tall. A trigger at the bottom of a drawer used to
 * open its menu off the screen, footer and all, with nothing to scroll.
 */
export function menuVertical(
  rect: { top: number; bottom: number },
  viewportHeight: number,
  preferredHeight: number,
): MenuVertical {
  const below = viewportHeight - rect.bottom - MENU_OFFSET_PX - VIEWPORT_MARGIN_PX;
  const above = rect.top - MENU_OFFSET_PX - VIEWPORT_MARGIN_PX;
  const fitsBelow = below >= Math.min(preferredHeight, MENU_MIN_USEFUL_HEIGHT_PX);
  if (fitsBelow || below >= above) {
    return { top: rect.bottom + MENU_OFFSET_PX, maxHeight: Math.max(0, Math.min(preferredHeight, below)) };
  }
  return {
    bottom: viewportHeight - rect.top + MENU_OFFSET_PX,
    maxHeight: Math.max(0, Math.min(preferredHeight, above)),
  };
}
